import React, { useEffect, useMemo, useState } from 'react';
import { clsx } from 'clsx';
import { useLocation } from 'react-router-dom';
import {
  BookOpen,
  ChevronRight,
  FileAudio,
  FileImage,
  FileText,
  Link as LinkIcon,
  Lock,
  Pencil,
  Plus,
  Trash2,
  Video,
  X,
} from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { useGuideStore } from '../../stores/guideStore';

const GUIDE_FEATURES = ['Document', 'Tools', 'eMarking', 'Analysis'];
const MEDIA_OPTIONS = [
  { value: 'Image', label: 'Image', icon: FileImage },
  { value: 'Video', label: 'Video (YouTube link)', icon: Video },
  { value: 'Document', label: 'Document', icon: FileText },
  { value: 'Audio', label: 'Audio', icon: FileAudio },
];

const EMPTY_FORM = {
  title: '',
  feature: 'Document',
  detail: '',
  mediaTypes: [],
  imageUrl: '',
  imagePath: '',
  imageFile: null,
  videoUrl: '',
  documentUrl: '',
  documentPath: '',
  documentFile: null,
  audioUrl: '',
  audioPath: '',
  audioFile: null,
  useCases: '',
};

const getGuideContext = (pathname) => {
  if (
    pathname === '/' ||
    pathname.startsWith('/document') ||
    pathname.startsWith('/assessment') ||
    pathname.startsWith('/shared')
  ) {
    return {
      pageKey: 'document-workspace',
      feature: 'Document',
      pageLabel: 'Document Workspace',
      description: 'Guides for documents, PDFs, assessments, and building steps.',
    };
  }

  if (pathname.startsWith('/tools')) {
    return {
      pageKey: 'tools',
      feature: 'Tools',
      pageLabel: 'Tools',
      description: 'Guides for built-in tools and shared classroom utilities.',
    };
  }

  if (pathname.startsWith('/marking') || pathname.startsWith('/grading')) {
    return {
      pageKey: 'emarking',
      feature: 'eMarking',
      pageLabel: 'eMarking',
      description: 'Guides for marking workflows, grading, and review queues.',
    };
  }

  if (pathname.startsWith('/analysis')) {
    return {
      pageKey: 'analysis',
      feature: 'Analysis',
      pageLabel: 'Analysis',
      description: 'Guides for marklist analysis and reporting views.',
    };
  }

  return {
    pageKey: 'all-features',
    feature: null,
    pageLabel: 'All Features',
    description: 'General product guides available across Sigma Classroom.',
  };
};

const parseUseCases = (value) =>
  String(value || '')
    .split(/\r?\n/)
    .map((item) => item.trim())
    .filter(Boolean);

const buildGuidePayload = (formState, userId, guideContext) => ({
  title: formState.title.trim(),
  pageKey: guideContext.pageKey,
  pageLabel: guideContext.pageLabel,
  feature: formState.feature,
  detail: formState.detail.trim(),
  mediaTypes: formState.mediaTypes,
  imageUrl: formState.mediaTypes.includes('Image') ? formState.imageUrl.trim() : '',
  imagePath: formState.mediaTypes.includes('Image') ? formState.imagePath.trim() : '',
  videoUrl: formState.mediaTypes.includes('Video') ? formState.videoUrl.trim() : '',
  documentUrl: formState.mediaTypes.includes('Document') ? formState.documentUrl.trim() : '',
  documentPath: formState.mediaTypes.includes('Document') ? formState.documentPath.trim() : '',
  audioUrl: formState.mediaTypes.includes('Audio') ? formState.audioUrl.trim() : '',
  audioPath: formState.mediaTypes.includes('Audio') ? formState.audioPath.trim() : '',
  useCases: parseUseCases(formState.useCases),
  createdBy: userId || null,
});

const getGuideMediaLinks = (guide) =>
  [
    guide.imageUrl ? { key: 'image', label: 'Image', href: guide.imageUrl, icon: FileImage } : null,
    guide.videoUrl ? { key: 'video', label: 'Video', href: guide.videoUrl, icon: Video } : null,
    guide.documentUrl ? { key: 'document', label: 'Document', href: guide.documentUrl, icon: FileText } : null,
    guide.audioUrl ? { key: 'audio', label: 'Audio', href: guide.audioUrl, icon: FileAudio } : null,
  ].filter(Boolean);

const GuideSidebarShell = ({ children }) => {
  const location = useLocation();
  const { user, isAdmin } = useAuthStore();
  const isUserAdmin = isAdmin();
  const {
    guides,
    isLoading,
    error,
    loadGuides,
    createGuide,
    updateGuide,
    deleteGuide,
    uploadGuideAsset,
    deleteGuideAsset,
    clearGuideError,
  } = useGuideStore();
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('list');
  const [formState, setFormState] = useState(EMPTY_FORM);
  const [editingGuideId, setEditingGuideId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [expandedGuideId, setExpandedGuideId] = useState(null);

  const guideContext = useMemo(() => getGuideContext(location.pathname), [location.pathname]);

  useEffect(() => {
    loadGuides().catch(() => {});
  }, [loadGuides]);

  useEffect(() => {
    setFormState((current) => ({
      ...current,
      feature: guideContext.feature || current.feature || 'Document',
    }));
  }, [guideContext.feature]);

  useEffect(() => {
    if (!isUserAdmin && activeTab === 'form') setActiveTab('list');
  }, [activeTab, isUserAdmin]);

  const visibleGuides = useMemo(() => {
    const pageScopedGuides = guides.filter((guide) => guide.pageKey === guideContext.pageKey);
    if (pageScopedGuides.length > 0) return pageScopedGuides;
    return guideContext.feature
      ? guides.filter((guide) => !guide.pageKey && guide.feature === guideContext.feature)
      : guides;
  }, [guideContext.feature, guideContext.pageKey, guides]);

  const resetForm = () => {
    setEditingGuideId(null);
    setSubmitError('');
    setFormState({ ...EMPTY_FORM, feature: guideContext.feature || 'Document' });
  };

  const handleToggleMedia = (value) => {
    setFormState((current) => {
      const exists = current.mediaTypes.includes(value);
      const nextState = {
        ...current,
        mediaTypes: exists
          ? current.mediaTypes.filter((item) => item !== value)
          : [...current.mediaTypes, value],
      };

      if (!exists) return nextState;
      if (value === 'Image') return { ...nextState, imageUrl: '', imagePath: '', imageFile: null };
      if (value === 'Document') return { ...nextState, documentUrl: '', documentPath: '', documentFile: null };
      if (value === 'Audio') return { ...nextState, audioUrl: '', audioPath: '', audioFile: null };
      if (value === 'Video') return { ...nextState, videoUrl: '' };
      return nextState;
    });
  };

  const handleEditGuide = (guide) => {
    setEditingGuideId(guide.id);
    setActiveTab('form');
    setIsOpen(true);
    setFormState({
      title: guide.title || '',
      feature: guide.feature || guideContext.feature || 'Document',
      detail: guide.detail || '',
      mediaTypes: Array.isArray(guide.mediaTypes) ? guide.mediaTypes : [],
      imageUrl: guide.imageUrl || '',
      imagePath: guide.imagePath || '',
      imageFile: null,
      videoUrl: guide.videoUrl || '',
      documentUrl: guide.documentUrl || '',
      documentPath: guide.documentPath || '',
      documentFile: null,
      audioUrl: guide.audioUrl || '',
      audioPath: guide.audioPath || '',
      audioFile: null,
      useCases: Array.isArray(guide.useCases) ? guide.useCases.join('\n') : '',
    });
  };

  const handleDeleteGuide = async (guideId) => {
    if (!window.confirm('Delete this guide?')) return;
    try {
      await deleteGuide(guideId);
      if (editingGuideId === guideId) resetForm();
      if (expandedGuideId === guideId) setExpandedGuideId(null);
    } catch (deleteError) {
      setSubmitError(deleteError.message || 'Failed to delete guide.');
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitError('');

    if (!formState.title.trim() || !formState.detail.trim()) {
      setSubmitError('Title and detail are required.');
      return;
    }

    try {
      setIsSubmitting(true);
      let nextFormState = { ...formState };
      const existingGuide = editingGuideId ? guides.find((guide) => guide.id === editingGuideId) : null;

      if (nextFormState.mediaTypes.includes('Image') && nextFormState.imageFile) {
        const uploaded = await uploadGuideAsset({
          file: nextFormState.imageFile,
          userId: user?.uid,
          mediaType: 'image',
          existingPath: nextFormState.imagePath || null,
        });
        nextFormState = {
          ...nextFormState,
          imageUrl: uploaded.url,
          imagePath: uploaded.path,
          imageFile: null,
        };
      }

      if (nextFormState.mediaTypes.includes('Document') && nextFormState.documentFile) {
        const uploaded = await uploadGuideAsset({
          file: nextFormState.documentFile,
          userId: user?.uid,
          mediaType: 'document',
          existingPath: nextFormState.documentPath || null,
        });
        nextFormState = {
          ...nextFormState,
          documentUrl: uploaded.url,
          documentPath: uploaded.path,
          documentFile: null,
        };
      }

      if (nextFormState.mediaTypes.includes('Audio') && nextFormState.audioFile) {
        const uploaded = await uploadGuideAsset({
          file: nextFormState.audioFile,
          userId: user?.uid,
          mediaType: 'audio',
          existingPath: nextFormState.audioPath || null,
        });
        nextFormState = {
          ...nextFormState,
          audioUrl: uploaded.url,
          audioPath: uploaded.path,
          audioFile: null,
        };
      }

      if (nextFormState.mediaTypes.includes('Image') && !nextFormState.imageUrl.trim()) {
        throw new Error('Upload an image file or remove Image from media options.');
      }
      if (nextFormState.mediaTypes.includes('Document') && !nextFormState.documentUrl.trim()) {
        throw new Error('Upload a document file or remove Document from media options.');
      }
      if (nextFormState.mediaTypes.includes('Audio') && !nextFormState.audioUrl.trim()) {
        throw new Error('Upload an audio file or remove Audio from media options.');
      }
      if (nextFormState.mediaTypes.includes('Video') && !nextFormState.videoUrl.trim()) {
        throw new Error('Add a YouTube link or remove Video from media options.');
      }

      if (!nextFormState.mediaTypes.includes('Image') && existingGuide?.imagePath) {
        await deleteGuideAsset(existingGuide.imagePath);
      }
      if (!nextFormState.mediaTypes.includes('Document') && existingGuide?.documentPath) {
        await deleteGuideAsset(existingGuide.documentPath);
      }
      if (!nextFormState.mediaTypes.includes('Audio') && existingGuide?.audioPath) {
        await deleteGuideAsset(existingGuide.audioPath);
      }

      const payload = buildGuidePayload(nextFormState, user?.uid, guideContext);
      if (editingGuideId) await updateGuide(editingGuideId, payload);
      else await createGuide(payload);
      setFormState(nextFormState);
      resetForm();
      setActiveTab('list');
    } catch (submitFailure) {
      setSubmitError(submitFailure.message || 'Failed to save guide.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="min-h-screen">{children}</div>

      <button
        type="button"
        onClick={() => {
          clearGuideError();
          setIsOpen(true);
        }}
        className="fixed right-2 bottom-0 z-40 inline-flex -translate-y-1/2 items-center gap-2 rounded-l-2xl rounded-r-md border border-sigma-200 bg-white px-3 py-3 text-sm font-semibold text-sigma-700 shadow-lg transition hover:border-sigma-300 hover:bg-sigma-50"
        aria-label="Open page guide"
      >
        <BookOpen className="h-4 w-4" />
        <span className="hidden sm:inline">Guide</span>
        <ChevronRight className="h-4 w-4" />
      </button>

      <div
        className={clsx(
          'fixed inset-0 z-50 bg-slate-900/40 transition-opacity',
          isOpen ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'
        )}
        onClick={() => setIsOpen(false)}
      />

      <aside
        className={clsx(
          'fixed right-0 top-0 z-50 flex h-full w-full max-w-md flex-col border-l border-slate-200 bg-white shadow-2xl transition-transform duration-300',
          isOpen ? 'translate-x-0' : 'translate-x-full'
        )}
      >
        <div className="border-b border-slate-200 bg-slate-50 px-5 py-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.2em] text-sigma-600">{guideContext.pageLabel}</div>
              <h2 className="mt-1 text-lg font-semibold text-slate-900">Feature Guide</h2>
              <p className="mt-1 text-sm text-slate-600">{guideContext.description}</p>
            </div>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="rounded-full border border-slate-200 p-2 text-slate-500 transition hover:bg-white hover:text-slate-700"
              aria-label="Close guide sidebar"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="mt-4 flex gap-2">
            <button
              type="button"
              onClick={() => setActiveTab('list')}
              className={clsx(
                'inline-flex items-center rounded-full px-3 py-1.5 text-sm font-medium transition',
                activeTab === 'list'
                  ? 'bg-sigma-600 text-white'
                  : 'bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-100'
              )}
            >
              Guide List
            </button>
            <button
              type="button"
              onClick={() => isUserAdmin && setActiveTab('form')}
              className={clsx(
                'inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-medium transition',
                activeTab === 'form'
                  ? 'bg-slate-900 text-white'
                  : 'bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-100',
                !isUserAdmin && 'cursor-not-allowed opacity-60'
              )}
            >
              {!isUserAdmin && <Lock className="h-3.5 w-3.5" />}
              Guide Form
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {activeTab === 'list' ? (
            <GuideList
              visibleGuides={visibleGuides}
              isLoading={isLoading}
              error={error}
              expandedGuideId={expandedGuideId}
              setExpandedGuideId={setExpandedGuideId}
              isUserAdmin={isUserAdmin}
              onEdit={handleEditGuide}
              onDelete={handleDeleteGuide}
            />
          ) : (
            <GuideForm
              formState={formState}
              setFormState={setFormState}
              isUserAdmin={isUserAdmin}
              isSubmitting={isSubmitting}
              submitError={submitError}
              editingGuideId={editingGuideId}
              onToggleMedia={handleToggleMedia}
              onSubmit={handleSubmit}
              onReset={resetForm}
            />
          )}
        </div>
      </aside>
    </>
  );
};

const GuideList = ({
  visibleGuides,
  isLoading,
  error,
  expandedGuideId,
  setExpandedGuideId,
  isUserAdmin,
  onEdit,
  onDelete,
}) => (
  <div className="space-y-4">
    {error ? <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div> : null}
    {isLoading && !visibleGuides.length ? <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-5 text-sm text-slate-500">Loading guides...</div> : null}
    {!isLoading && visibleGuides.length === 0 ? <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-5 text-sm text-slate-600">No guides yet for this area.</div> : null}
    {visibleGuides.map((guide) => {
      const isExpanded = expandedGuideId === guide.id;
      const mediaLinks = getGuideMediaLinks(guide);
      return (
        <article key={guide.id} className="rounded-2xl border border-slate-200 bg-white shadow-sm">
          <button type="button" onClick={() => setExpandedGuideId(isExpanded ? null : guide.id)} className="flex w-full items-start justify-between gap-4 px-4 py-4 text-left">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-sigma-600">{guide.feature}</div>
              <h3 className="mt-1 text-sm font-semibold text-slate-900">{guide.title}</h3>
            </div>
            <ChevronRight className={clsx('mt-1 h-4 w-4 text-slate-400 transition-transform', isExpanded && 'rotate-90')} />
          </button>
          {isExpanded ? (
            <div className="border-t border-slate-100 px-4 py-4 text-sm text-slate-700">
              <p className="whitespace-pre-wrap leading-6">{guide.detail}</p>
              {Array.isArray(guide.useCases) && guide.useCases.length > 0 ? <div className="mt-4 flex flex-wrap gap-2">{guide.useCases.map((useCase) => <span key={useCase} className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">{useCase}</span>)}</div> : null}
              {mediaLinks.length > 0 ? <div className="mt-4 space-y-2">{mediaLinks.map((media) => { const Icon = media.icon; return <a key={media.key} href={media.href} target="_blank" rel="noreferrer" className="flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 transition hover:bg-slate-50"><Icon className="h-4 w-4 text-sigma-600" /><span>{media.label}</span><LinkIcon className="ml-auto h-3.5 w-3.5 text-slate-400" /></a>; })}</div> : null}
              {isUserAdmin ? <div className="mt-4 flex gap-2"><button type="button" onClick={() => onEdit(guide)} className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"><Pencil className="h-3.5 w-3.5" />Edit</button><button type="button" onClick={() => onDelete(guide.id)} className="inline-flex items-center gap-2 rounded-full border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-700 transition hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" />Delete</button></div> : null}
            </div>
          ) : null}
        </article>
      );
    })}
  </div>
);

const GuideForm = ({ formState, setFormState, isUserAdmin, isSubmitting, submitError, editingGuideId, onToggleMedia, onSubmit, onReset }) => (
  <form onSubmit={onSubmit} className="space-y-5">
    {!isUserAdmin ? <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">Only admins can create or edit guides.</div> : null}
    {submitError ? <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{submitError}</div> : null}
    <GuideInput label="Title"><input type="text" value={formState.title} onChange={(event) => setFormState((current) => ({ ...current, title: event.target.value }))} className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:border-sigma-500 focus:outline-none focus:ring-2 focus:ring-sigma-200" placeholder="Creating a standard document" disabled={!isUserAdmin || isSubmitting} /></GuideInput>
    <GuideInput label="Feature"><select value={formState.feature} onChange={(event) => setFormState((current) => ({ ...current, feature: event.target.value }))} className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:border-sigma-500 focus:outline-none focus:ring-2 focus:ring-sigma-200" disabled={!isUserAdmin || isSubmitting}>{GUIDE_FEATURES.map((feature) => <option key={feature} value={feature}>{feature}</option>)}</select></GuideInput>
    <GuideInput label="Detail"><textarea value={formState.detail} onChange={(event) => setFormState((current) => ({ ...current, detail: event.target.value }))} rows={5} className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:border-sigma-500 focus:outline-none focus:ring-2 focus:ring-sigma-200" placeholder="Explain the workflow, important controls, and expected result." disabled={!isUserAdmin || isSubmitting} /></GuideInput>
    <div><div className="text-sm font-medium text-slate-700">Media Options</div><div className="mt-2 grid grid-cols-2 gap-2">{MEDIA_OPTIONS.map((option) => { const Icon = option.icon; const checked = formState.mediaTypes.includes(option.value); return <button key={option.value} type="button" onClick={() => onToggleMedia(option.value)} disabled={!isUserAdmin || isSubmitting} className={clsx('flex items-center gap-2 rounded-xl border px-3 py-2 text-left text-sm transition', checked ? 'border-sigma-500 bg-sigma-50 text-sigma-800' : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50', (!isUserAdmin || isSubmitting) && 'cursor-not-allowed opacity-60')}><Icon className="h-4 w-4" /><span>{option.label}</span></button>; })}</div></div>
    {formState.mediaTypes.includes('Image') ? <GuideFileField label="Image Upload" currentUrl={formState.imageUrl} file={formState.imageFile} accept="image/*" disabled={!isUserAdmin || isSubmitting} onFileChange={(file) => setFormState((current) => ({ ...current, imageFile: file }))} /> : null}
    {formState.mediaTypes.includes('Video') ? <GuideUrlField label="Video URL" value={formState.videoUrl} onChange={(value) => setFormState((current) => ({ ...current, videoUrl: value }))} disabled={!isUserAdmin || isSubmitting} placeholder="https://youtube.com/..." /> : null}
    {formState.mediaTypes.includes('Document') ? <GuideFileField label="Document Upload" currentUrl={formState.documentUrl} file={formState.documentFile} accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.rtf,.csv,application/*,.zip" disabled={!isUserAdmin || isSubmitting} onFileChange={(file) => setFormState((current) => ({ ...current, documentFile: file }))} /> : null}
    {formState.mediaTypes.includes('Audio') ? <GuideFileField label="Audio Upload" currentUrl={formState.audioUrl} file={formState.audioFile} accept="audio/*" disabled={!isUserAdmin || isSubmitting} onFileChange={(file) => setFormState((current) => ({ ...current, audioFile: file }))} /> : null}
    <GuideInput label="Use Cases"><textarea value={formState.useCases} onChange={(event) => setFormState((current) => ({ ...current, useCases: event.target.value }))} rows={4} className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:border-sigma-500 focus:outline-none focus:ring-2 focus:ring-sigma-200" placeholder={'One use case per line\nLesson notes\nAssessment handout'} disabled={!isUserAdmin || isSubmitting} /></GuideInput>
    <div className="flex gap-2">
      <button type="submit" disabled={!isUserAdmin || isSubmitting} className={clsx('inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold text-white transition', isUserAdmin && !isSubmitting ? 'bg-sigma-600 hover:bg-sigma-700' : 'cursor-not-allowed bg-slate-300')}>{editingGuideId ? <Pencil className="h-4 w-4" /> : <Plus className="h-4 w-4" />}{editingGuideId ? 'Update Guide' : 'Create Guide'}</button>
      <button type="button" onClick={onReset} disabled={!isUserAdmin || isSubmitting} className="rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60">Reset</button>
    </div>
  </form>
);

const GuideInput = ({ label, children }) => <label className="block"><span className="text-sm font-medium text-slate-700">{label}</span>{children}</label>;

const GuideUrlField = ({ label, value, onChange, disabled, placeholder }) => (
  <GuideInput label={label}>
    <input type="url" value={value} onChange={(event) => onChange(event.target.value)} className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:border-sigma-500 focus:outline-none focus:ring-2 focus:ring-sigma-200" placeholder={placeholder} disabled={disabled} />
  </GuideInput>
);

const GuideFileField = ({ label, currentUrl, file, accept, disabled, onFileChange }) => (
  <GuideInput label={label}>
    <input
      type="file"
      accept={accept}
      onChange={(event) => onFileChange(event.target.files?.[0] || null)}
      className="mt-1 block w-full text-sm text-slate-700 file:mr-3 file:rounded-full file:border-0 file:bg-sigma-100 file:px-3 file:py-2 file:text-sm file:font-semibold file:text-sigma-700 hover:file:bg-sigma-200"
      disabled={disabled}
    />
    <div className="mt-2 text-xs text-slate-500">
      {file ? `Selected: ${file.name}` : currentUrl ? 'Existing file will be kept until you replace it.' : 'No file uploaded yet.'}
    </div>
    {currentUrl ? <a href={currentUrl} target="_blank" rel="noreferrer" className="mt-2 inline-flex text-xs font-medium text-sigma-700 hover:text-sigma-900">Open current file</a> : null}
  </GuideInput>
);

export default GuideSidebarShell;
